import { ALARM_TYPES, ALERT_TIME_ZONE, roleAtLeast, type Alarm, type AlarmType, type CurrentUser, type TenantRole } from "@fts/core";
import type { AlertPerson } from "../data/provider";
import type { MessageKey, TFn } from "../i18n";

/** Alarms are acknowledged by safety (İSG) and above on the alarm's site (every visible site is accessible). */
export const canAcknowledge = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "safety");

/**
 * May the user acknowledge THIS alarm? Same rule as acknowledge_alarm(): safety and above; an alarm whose
 * site was deleted (siteId null) only by members without a site scope (and platform admins).
 */
export function canAcknowledgeAlarm(user: CurrentUser | null, tenantId: string, role: TenantRole | null, alarm: Pick<Alarm, "siteId">): boolean {
  if (!canAcknowledge(role)) return false;
  if (alarm.siteId !== null) return true;
  if (!user) return false;
  if (user.platformAdmin) return true;
  const m = user.memberships.find((x) => x.tenantId === tenantId);
  return !!m && m.siteIds.length === 0;
}

/** Working hours and night contacts: fleet managers and above on the site. */
export const canEditAlertSettings = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "fleet_manager");

const isAlarmType = (t: string): t is AlarmType => (ALARM_TYPES as readonly string[]).includes(t);

export function alarmTypeLabel(t: TFn, type: string): string {
  return isAlarmType(type) ? t(`alarm.type.${type}` as MessageKey) : t("alarm.type.other");
}

/** "Darbe · Seviye 3" for impacts; the plain type for the rest (their level carries no meaning). */
export function alarmTitle(t: TFn, a: Pick<Alarm, "type" | "severity">): string {
  const type = alarmTypeLabel(t, a.type);
  return a.type === "impact" ? t("alarm.typeWithSeverity", { type, level: a.severity }) : type;
}

export const SEVERITY_TONE: Record<number, string> = { 1: "info", 2: "warn", 3: "crit" };

export function alarmTone(a: Pick<Alarm, "type" | "severity" | "status">): string {
  if (a.status === "acknowledged") return "neutral";
  if (a.type === "impact") return SEVERITY_TONE[a.severity] ?? "warn";
  return "warn";
}

/** Date and time in Turkey time, whatever the browser's zone. */
export function formatAlarmTime(iso: string | null, locale: string, withSeconds = false): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(locale, {
    timeZone: ALERT_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
  });
}

/** Weekday and time in Turkey time ("Pzt 19 Eki 07:00"), for digest previews. */
export function formatAlarmWhen(d: Date, locale: string): string {
  return d.toLocaleString(locale, { timeZone: ALERT_TIME_ZONE, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Time of day in Turkey time ("14:05"). */
export function formatClock(d: Date, locale: string): string {
  return d.toLocaleTimeString(locale, { timeZone: ALERT_TIME_ZONE, hour: "2-digit", minute: "2-digit" });
}

/** Compact day list: consecutive runs become ranges ("Pzt–Cum", "Pzt, Çar–Cum"). */
export function formatDays(t: TFn, days: number[]): string {
  const sorted = [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
  const runs: Array<[number, number]> = [];
  for (const d of sorted) {
    const last = runs[runs.length - 1];
    if (last && d === last[1] + 1) last[1] = d;
    else runs.push([d, d]);
  }
  const name = (d: number) => t(`day.short.${d}` as MessageKey);
  return runs
    .map(([a, b]) => (a === b ? name(a) : b === a + 1 ? `${name(a)}, ${name(b)}` : `${name(a)}–${name(b)}`))
    .join(", ");
}

/** "A, B ve C" / "A, B and C". */
export function joinList(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(items);
  } catch {
    return items.join(", ");
  }
}

/** Display name of a console user, or a neutral label when RLS hides their profile. */
export function personName(t: TFn, people: ReadonlyArray<Pick<AlertPerson, "userId" | "displayName">>, userId: string | null, meId?: string): string {
  if (!userId) return t("common.empty");
  const p = people.find((x) => x.userId === userId);
  const name = p?.displayName ?? t("alarmNotify.unknownUser");
  return meId && userId === meId ? t("alarmNotify.you", { name }) : name;
}

/** The newest open alarm of each vehicle (live map card link). */
export function latestOpenByVehicle(alarms: Alarm[]): Map<string, Alarm> {
  const out = new Map<string, Alarm>();
  for (const a of alarms) {
    if (a.status !== "open" || !a.vehicleId) continue;
    const prev = out.get(a.vehicleId);
    if (!prev || Date.parse(a.occurredAt) > Date.parse(prev.occurredAt)) out.set(a.vehicleId, a);
  }
  return out;
}

/** Alarm list filters as kept in the URL. Missing status means "open". */
export type AlarmStatusFilter = "open" | "acknowledged" | "all";

export function parseStatusFilter(v: string | null): AlarmStatusFilter {
  return v === "acknowledged" || v === "all" ? v : "open";
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
export const validDay = (v: string | null): string | null => (v && DAY.test(v) ? v : null);
